import { failureNotification } from "../store/notification.duck";
import { NotificationMessages } from "@shared/models/notification.models";

export function safePrintError(err: any, dispatch: any, fallBackMessage?: string) {
  try {
    if (err.message) {
      const parsed = JSON.parse(err.message);
      if (parsed.message) {
        dispatch(failureNotification(parsed.message));
        return;
      }
    }
  } catch (e: any) {

  }
  dispatch(failureNotification(fallBackMessage || 'Action failed'));
}

export function parseErrorFromException(err: any): string {
  try {
    return JSON.parse(err.message).message
  } catch (e) {
    TrackJS?.track(`Unknown error: ${JSON.stringify(err)}`);
    return `Unknown error: ${JSON.stringify(err)}`;
  }
}

export enum ErrorCode {
  VerificationRequired,
  Unknown,
  TeamNameAlreadyExists,
  EmailAlreadyExists,
  FailedLogin,
  OfficeContainsArea,
  LoginRequired,
  UserDeleted,
  UserAlreadyInAccount,
}

export interface Error {
  title: string;
  description: string;
  id: ErrorCode;
}

const messages: any = {
  'Email verification required': {
    title: 'Email verification required',
    description: 'Check your email for a verification email.',
    id: ErrorCode.VerificationRequired,
  },
  'default': {
    title: 'Unknown Error',
    description: '',
    id: ErrorCode.Unknown,
  },
  'unk': {
    title: 'Unknown Error',
    description: '',
    id: ErrorCode.Unknown,
  },
  'Team already exists': {
    title: 'Team already exists',
    description: 'You already have a team with that name.',
    id: ErrorCode.TeamNameAlreadyExists,
  },
  'Email already exists': {
    title: 'Email already exists',
    description: 'That email address is already taken.',
    id: ErrorCode.EmailAlreadyExists,
  },
  // Must match EmailAlreadyExistsException.USER_IN_SAME_COMPANY_MESSAGE on the backend.
  'This account already exists. Go to Settings > Manage Users to assign them to the correct team.': {
    title: 'This account already exists',
    description: 'Go to Settings > Manage Users to assign them to the correct team.',
    id: ErrorCode.UserAlreadyInAccount,
  },
  // Must match EmailAlreadyExistsException.USER_DELETED_MESSAGE on the backend.
  'Account already exists. This user was previously deleted. You can restore their access under Settings > Manage Users > Deleted Users.': {
    title: 'Account already exists',
    description: 'This user was previously deleted. You can restore their access under Settings > Manage Users > Deleted Users.',
    id: ErrorCode.UserDeleted,
  },
  'Failed login': {
    title: 'Invalid email or password',
    description: "Try clicking 'Forgot Password' if you're having trouble logging in.",
    id: ErrorCode.FailedLogin,
  },
  'Login failed - account users Google': {
    title: 'Wrong type of log in used.',
    description: "This account uses Google to log in. Select 'Log in with Google' and follow the steps to log into this account.",
    id: ErrorCode.FailedLogin,
  },
  'Login failed - account users Microsoft 365': {
    title: 'Wrong type of log in used.',
    description: "This account uses Microsoft 365 to log in. Select 'Log in with Microsoft 365' and follow the steps to log into this account.",
    id: ErrorCode.FailedLogin,
  },
  'This office contains an area': {
    title: 'This office contains an area.',
    description: "You cannot delete an office with an associated area. Please contact hello@team-today.com to make amends to this office.",
    id: ErrorCode.OfficeContainsArea,
  },
  'reg': {
    title: 'No account registered.',
    description: "You can register for an account or ask a colleague for an invite. If you think you already have an account please contact hello@team-today.com.",
    id: ErrorCode.OfficeContainsArea,
  },
  'azu': {
    title: 'Failed to log in with Microsoft.',
    description: "Failed to log in with Microsoft, please try again or is the problem persists contact hello@team-today.com.",
    id: ErrorCode.FailedLogin,
  },
  'goog': {
    title: 'Failed to log in with Google.',
    description: "Failed to log in with Google, please try again or is the problem persists contact hello@team-today.com.",
    id: ErrorCode.FailedLogin,
  },
  '401': {
    title: '',
    description: "",
    id: ErrorCode.LoginRequired,
  },
  'canByUser': {
    title: 'Failed to log in with Microsoft.',
    description: "This step is required to advance, a popup will appear and you can use your Microsoft account to log in. If you have any questions contact us at hello@team-today.com",
    id: ErrorCode.FailedLogin,
  },
  'consent': {
    title: 'Admin approval needed.',
    description: 'Your organisation has not yet approved Team Today. Please open Team Today at app.team-today.com to complete sign-in, or ask your IT admin to approve the app.',
    id: ErrorCode.FailedLogin,
  },
  'interact': {
    title: "Sign-in can't complete inside Outlook.",
    description: 'Please open Team Today at app.team-today.com to finish signing in. Once signed in there, returning to Outlook will work.',
    id: ErrorCode.FailedLogin,
  },
  'embBlock': {
    title: "Sign-in popup couldn't open.",
    description: 'Outlook prevented the sign-in window from opening. Please use the web version at app.team-today.com to sign in.',
    id: ErrorCode.FailedLogin,
  },
}

export function classifyAuthError(err: any): NotificationMessages {
  const msg = (err?.message ?? err?.toString?.() ?? '').toString();
  const code = err?.errorCode as string | undefined;

  if (msg.includes('CancelledByUser')) return NotificationMessages.cancelledByUser;
  if (msg.includes('User not registered')) return NotificationMessages.noAccountFound;

  if (code === 'consent_required'
    || msg.includes('AADSTS65001')
    || msg.includes('consent_required')
    || msg.includes('Consent required')
    || msg.includes('resourceRequiresConsent')) {
    return NotificationMessages.consentRequired;
  }

  if (msg.includes('URL was unable to be opened in embedded browser')
    || msg.includes('FailedToOpenWindow')) {
    return NotificationMessages.embeddedBrowserBlocked;
  }

  if (code === 'interaction_required'
    || msg.includes('interaction_required')
    || msg.includes('login_required')) {
    return NotificationMessages.interactionRequired;
  }

  return NotificationMessages.unknownError;
}

function defaultError(message: string) {
  return {
    title: message,
    description: message,
    id: ErrorCode.Unknown,
  }
}

export function enrichError(message: string | undefined): Error {
  if (!message) {
    return messages('default');
  }
  if (messages[message]) {
    return messages[message]
  } else {
    return defaultError(message);
  }
}
